import { useState, useEffect } from 'react';

import toast from 'react-hot-toast';
import { Loader2, Building2, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { workspaceMembersApi } from '@/lib/api/workspace-members.api';
import { billingApi, type PricingResponse } from '@/lib/api/billing';
import { useWorkspace } from '@/contexts/workspace-context';
import { cn } from '@/lib/utils';

type PlanChoice = 'standard' | 'pro';
type IntervalChoice = 'month' | 'year';

interface CreateWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (workspaceId: string) => void;
}

/**
 * Create Workspace Dialog
 * First workspace: name only (free plan).
 * 2nd+ workspace: name + plan selection (Standard/Pro, Monthly/Yearly).
 * Subscription created server-side using saved Stripe payment method.
 */
export function CreateWorkspaceDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateWorkspaceDialogProps) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [planType, setPlanType] = useState<PlanChoice>('standard');
  const [interval, setInterval] = useState<IntervalChoice>('month');
  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  const { ownedWorkspaceCount } = useWorkspace();
  const isAdditionalWorkspace = ownedWorkspaceCount >= 1;

  // Fetch pricing when dialog opens and user needs plan selection
  useEffect(() => {
    if (open && isAdditionalWorkspace && !pricing) {
      setPricingLoading(true);
      billingApi
        .getPricing()
        .then(setPricing)
        .catch(() => toast.error('Failed to load pricing'))
        .finally(() => setPricingLoading(false));
    }
  }, [open, isAdditionalWorkspace, pricing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Workspace name is required');
      return;
    }

    try {
      setLoading(true);
      if (isAdditionalWorkspace) {
        // Redirect to Stripe Checkout — workspace created after payment via webhook
        const { url } = await workspaceMembersApi.createWorkspaceWithPlan(name, planType, interval);
        window.location.href = url;
        return; // Don't reset loading — page is navigating away
      }
      const result = await workspaceMembersApi.createWorkspace(name);
      toast.success('Workspace created');
      onSuccess(result.workspace.id);
      handleClose();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to create workspace');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setPlanType('standard');
    setInterval('month');
    onOpenChange(false);
  };

  // Format price from cents to display string
  const formatPrice = (cents: number): string => {
    return `$${(cents / 100).toFixed(0)}`;
  };

  // Get price for a plan + interval combo
  const getPrice = (plan: PlanChoice, int: IntervalChoice): string => {
    if (!pricing) return '...';
    const planPricing = pricing.plans[plan];
    const cents = int === 'month' ? planPricing.monthly.usd : planPricing.yearly.usd;
    if (cents === undefined) return '...';
    return formatPrice(cents);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Create Workspace
          </DialogTitle>
          <DialogDescription>
            {isAdditionalWorkspace
              ? 'Create a new workspace with a paid plan. Your existing payment method will be used.'
              : "Create a new workspace to organize your projects. You'll be the owner and can invite team members."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Workspace name</Label>
            <Input
              id="workspace-name"
              placeholder="My Company"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          {/* Plan selection for 2nd+ workspace */}
          {isAdditionalWorkspace && (
            <>
              {/* Billing interval toggle */}
              <div className="flex items-center justify-center gap-2">
                <button
                  type="button"
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                    interval === 'month'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                  onClick={() => setInterval('month')}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                    interval === 'year'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                  onClick={() => setInterval('year')}
                >
                  Yearly
                </button>
              </div>

              {/* Plan cards */}
              {pricingLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {/* Standard Plan */}
                  <button
                    type="button"
                    onClick={() => setPlanType('standard')}
                    className={cn(
                      'relative rounded-lg border p-4 text-left transition-all',
                      planType === 'standard'
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:border-muted-foreground/50',
                    )}
                  >
                    {planType === 'standard' && (
                      <div className="absolute top-2 right-2">
                        <Check className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <p className="text-sm font-semibold">Standard</p>
                    <p className="text-2xl font-bold mt-1">
                      {getPrice('standard', interval)}
                      <span className="text-xs font-normal text-muted-foreground">
                        /{interval === 'month' ? 'mo' : 'yr'}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      100 credits/month
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Haiku & Sonnet models
                    </p>
                  </button>

                  {/* Pro Plan */}
                  <button
                    type="button"
                    onClick={() => setPlanType('pro')}
                    className={cn(
                      'relative rounded-lg border p-4 text-left transition-all',
                      planType === 'pro'
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border hover:border-muted-foreground/50',
                    )}
                  >
                    {planType === 'pro' && (
                      <div className="absolute top-2 right-2">
                        <Check className="h-4 w-4 text-primary" />
                      </div>
                    )}
                    <p className="text-sm font-semibold">Pro</p>
                    <p className="text-2xl font-bold mt-1">
                      {getPrice('pro', interval)}
                      <span className="text-xs font-normal text-muted-foreground">
                        /{interval === 'month' ? 'mo' : 'yr'}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      200 credits/month
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Haiku, Sonnet & Opus
                    </p>
                  </button>
                </div>
              )}
            </>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || (isAdditionalWorkspace && pricingLoading)}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : isAdditionalWorkspace ? (
                'Create & Subscribe'
              ) : (
                'Create Workspace'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
